import React, { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { FiCheck, FiChevronDown, FiMoreVertical, FiSearch, FiUser, FiUserMinus, FiX } from "react-icons/fi";
import { Skeleton } from "../../../components/UI";
import { useLanguage } from "../../../contexts/LanguageContext";
import { useDismiss } from "../../../hooks/useDismiss";
import { menuKeys } from "../../../hooks/menuKeys";
import { IconAction, Num, PlayerAvatar, Presence } from "./primitives";

export type Person = {
  id: string;
  username: string;
  avatar?: string;
  elo?: number;
  status?: string;
  relation?: string;
  user_id?: string;
};

/* ───────────────────────── строка игрока ───────────────────────── */
const Row = styled.li`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  min-height: 56px;
  padding: 6px 2px 6px 6px;
  border-radius: 12px;
  transition: background ${(p) => p.theme.motion.fast};
  &:hover,
  &:focus-within {
    --bg: ${(p) => p.theme.colors.raised};
    background: ${(p) => p.theme.colors.raised};
  }
`;
const Who = styled.div`
  min-width: 0;
  display: grid;
  gap: 1px;
  a {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 15px;
    line-height: 20px;
    font-weight: 600;
  }
  &&& a {
    color: ${(p) => p.theme.colors.text};
  }
  a:hover {
    text-decoration: underline;
    text-underline-offset: 3px;
  }
  small {
    font-size: 13px;
    line-height: 18px;
    color: ${(p) => p.theme.colors.textMuted};
  }
`;
const Actions = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 2px;
`;
const Pill = styled.button<{ $tone?: "accent" | "quiet" }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 0 10px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 650;
  white-space: nowrap;
  color: ${(p) => (p.$tone === "quiet" ? p.theme.colors.textSecondary : p.theme.colors.primary)};
  background: ${(p) => (p.$tone === "quiet" ? "transparent" : p.theme.colors.primarySoft)};
  transition:
    background ${(p) => p.theme.motion.fast},
    color ${(p) => p.theme.motion.fast};
  &:hover:not(:disabled) {
    background: ${(p) => (p.$tone === "quiet" ? p.theme.colors.surfaceHover : p.theme.colors.primary)};
    color: ${(p) => (p.$tone === "quiet" ? p.theme.colors.text : p.theme.colors.onPrimary)};
  }
  &:disabled {
    cursor: not-allowed;
    color: ${(p) => p.theme.colors.textMuted};
    background: ${(p) => p.theme.colors.input};
  }
  @media (pointer: coarse) {
    min-height: 44px;
  }
`;
const Status = styled.span<{ $tone?: "ok" | "muted" }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 0 8px;
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => (p.$tone === "ok" ? p.theme.colors.success : p.theme.colors.textMuted)};
  animation: ${keyframes`from{opacity:0}to{opacity:1}`} 0.18s linear;
  svg {
    width: 14px;
    height: 14px;
  }
`;
const RequestRow = styled.li`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 4px 10px;
  padding: 6px;
  .nick {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 15px;
    line-height: 20px;
    font-weight: 600;
  }
  .req {
    grid-column: 2;
    gap: 8px;
  }
`;
const List = styled.ul`
  list-style: none;
  display: grid;
  gap: 2px;
`;
const Menu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 5;
  min-width: 220px;
  padding: 6px;
  border-radius: 12px;
  background: ${(p) => p.theme.colors.surfaceHover};
  border: 1px solid ${(p) => p.theme.colors.border};
  box-shadow: 0 16px 40px #0009;
  a,
  button {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 44px;
    padding: 0 10px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    color: ${(p) => p.theme.colors.text};
    text-align: left;
    &:hover,
    &:focus-visible {
      background: ${(p) => p.theme.colors.input};
    }
    svg {
      width: 16px;
      height: 16px;
      color: ${(p) => p.theme.colors.textSecondary};
    }
  }
`;

function RowMenu({ f, onRemove }: { f: Person; onRemove: () => void }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, box, btn);
  return (
    <Actions ref={box}>
      <IconAction
        ref={btn}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("home.row_actions", { name: f.username })}
        title={t("home.row_actions", { name: f.username })}
        onClick={() => setOpen((v) => !v)}
      >
        <FiMoreVertical />
      </IconAction>
      {open && (
        <Menu role="menu" aria-label={f.username} onKeyDown={menuKeys} ref={(el) => el?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()}>
          <Link role="menuitem" to={"/profile/" + f.id} onClick={close}>
            <FiUser aria-hidden="true" />
            {t("home.view_profile")}
          </Link>
          <button
            type="button"
            role="menuitem"
            aria-label={t("home.remove_named", { name: f.username })}
            onClick={() => {
              close();
              onRemove();
            }}
          >
            <FiUserMinus aria-hidden="true" />
            {t("home.remove_friend")}
          </button>
        </Menu>
      )}
    </Actions>
  );
}

/* Контекст приглашения: единая модель для панели, sheet и диалога. */
export type InviteCtx = {
  partyIds: Set<string>;
  invited: Set<string>;
  full: boolean;
  searching: boolean;
  locked: boolean;
};

export function inviteAction(f: Person, ctx: InviteCtx, t: (k: string, p?: Record<string, string | number>) => string, onInvite: () => void) {
  if (ctx.partyIds.has(f.id)) return <Status $tone="ok"><FiCheck aria-hidden="true" />{t("home.friend_in_party")}</Status>;
  if (ctx.invited.has(f.id)) return <Status $tone="ok" role="status"><FiCheck aria-hidden="true" />{t("home.invited")}</Status>;
  const why = ctx.searching ? t("home.invite_searching") : ctx.full ? t("home.invite_full") : "";
  return (
    <Pill
      type="button"
      disabled={ctx.locked || !!why}
      title={why || undefined}
      aria-label={t("home.invite_named", { name: f.username })}
      onClick={onInvite}
    >
      {t("home.invite_short")}
    </Pill>
  );
}

export function FriendRow({ f, ctx, onInvite, onRemove }: { f: Person; ctx: InviteCtx; onInvite: () => void; onRemove?: () => void }) {
  const { t } = useLanguage();
  const status = (f.status || "offline") as Presence;
  return (
    <Row>
      <PlayerAvatar name={f.username} src={f.avatar} size={40} presence={status} />
      <Who>
        <Link to={"/profile/" + f.id} title={f.username}>
          {f.username}
        </Link>
        <small>
          <span className="sr-only">{t(`home.status.${status}`)}. </span>
          {status === "in_game" && <>{t("home.status.in_game")} · </>}
          {typeof f.elo === "number" && (
            <Num>
              {f.elo} {t("home.elo")}
            </Num>
          )}
        </small>
      </Who>
      <Actions>
        {inviteAction(f, ctx, t, onInvite)}
        {onRemove && <RowMenu f={f} onRemove={onRemove} />}
      </Actions>
    </Row>
  );
}

/* ───────────────────────── поиск ───────────────────────── */
const SearchWrap = styled.label`
  position: relative;
  display: block;
  > svg {
    position: absolute;
    left: 14px;
    top: 50%;
    width: 18px;
    height: 18px;
    transform: translateY(-50%);
    color: ${(p) => p.theme.colors.textMuted};
    pointer-events: none;
  }
  input {
    width: 100%;
    height: 44px;
    padding: 0 44px 0 42px;
    border-radius: 10px;
    border: 1px solid ${(p) => p.theme.colors.border};
    background: ${(p) => p.theme.colors.input};
    color: ${(p) => p.theme.colors.text};
    font-size: 15px;
    transition:
      border-color ${(p) => p.theme.motion.fast},
      background ${(p) => p.theme.motion.fast};
    &::placeholder {
      color: ${(p) => p.theme.colors.textMuted};
    }
    &:hover {
      border-color: ${(p) => p.theme.colors.borderControl};
    }
    &:focus-visible {
      outline: none;
      border-color: ${(p) => p.theme.colors.primary};
      box-shadow: 0 0 0 3px ${(p) => p.theme.colors.primarySoft};
    }
    &::-webkit-search-cancel-button {
      display: none;
    }
  }
  button {
    position: absolute;
    right: 0;
    top: 0;
    width: 44px;
    height: 44px;
  }
`;

export function SearchBox({ value, onChange, inputRef }: { value: string; onChange: (v: string) => void; inputRef?: React.Ref<HTMLInputElement> }) {
  const { t } = useLanguage();
  return (
    <SearchWrap>
      <FiSearch aria-hidden="true" />
      <input
        ref={inputRef}
        type="search"
        aria-label={t("home.search_players_label")}
        placeholder={t("home.search_placeholder")}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {value && (
        <IconAction type="button" aria-label={t("home.search_clear")} onClick={() => onChange("")}>
          <FiX />
        </IconAction>
      )}
    </SearchWrap>
  );
}

export function SearchResults({
  q,
  found,
  searching,
  ctx,
  onAdd,
  onInvite,
  onClear,
}: {
  q: string;
  found: Person[];
  searching: boolean;
  ctx: InviteCtx;
  onAdd: (id: string) => void;
  onInvite: (id: string) => void;
  onClear: () => void;
}) {
  const { t } = useLanguage();
  if (searching && !found.length)
    return (
      <div role="status" aria-label={t("home.searching_players")} style={{ display: "grid", gap: 8 }}>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} $h={56} $r={12} />
        ))}
      </div>
    );
  if (!found.length)
    return (
      <Empty role="status" style={{ paddingTop: 16 }}>
        <h3>{t("home.search_none")}</h3>
        <p>«{q.trim()}»</p>
        <button type="button" onClick={onClear}>
          {t("home.search_clear")}
        </button>
      </Empty>
    );
  return (
    <List aria-busy={searching} aria-label={t("home.find_players")}>
      {found.map((f) => {
        const friend = f.relation === "friends";
        return (
          <Row key={f.id}>
            <PlayerAvatar name={f.username} src={f.avatar} size={40} />
            <Who>
              <Link to={"/profile/" + f.id} title={f.username}>
                {f.username}
              </Link>
              {typeof f.elo === "number" && (
                <small>
                  <Num>
                    {f.elo} {t("home.elo")}
                  </Num>
                </small>
              )}
            </Who>
            <Actions>
              {friend ? (
                inviteAction(f, ctx, t, () => onInvite(f.id))
              ) : f.relation === "pending_out" ? (
                <Status>{t("home.friend_pending")}</Status>
              ) : f.relation === "pending_in" ? (
                <Status>{t("home.friend_incoming")}</Status>
              ) : (
                <Pill type="button" aria-label={t("home.add_friend_named", { name: f.username })} onClick={() => onAdd(f.id)}>
                  {t("home.add_friend")}
                </Pill>
              )}
            </Actions>
          </Row>
        );
      })}
    </List>
  );
}

/* ───────────────────────── пустое состояние и группы ───────────────────────── */
const Empty = styled.div`
  display: grid;
  justify-items: center;
  gap: 8px;
  padding: 24px 12px 8px;
  text-align: center;
  h3 {
    font-size: 15px;
    line-height: 22px;
    font-weight: 600;
  }
  p {
    max-width: 30ch;
    font-size: 14px;
    line-height: 20px;
    color: ${(p) => p.theme.colors.textMuted};
  }
  .stack {
    display: flex;
    margin-bottom: 4px;
    > * {
      margin-left: -10px;
      box-shadow: 0 0 0 3px ${(p) => p.theme.colors.surface};
    }
    > :first-child {
      margin-left: 0;
    }
  }
  button {
    margin-top: 8px;
    min-height: 44px;
    padding: 0 16px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    color: ${(p) => p.theme.colors.text};
    background: ${(p) => p.theme.colors.surfaceHover};
    transition: background ${(p) => p.theme.motion.fast};
    &:hover {
      background: #2d3b4b;
    }
  }
`;
const Ghost = styled.span`
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: ${(p) => p.theme.colors.navigation};
  border: 1px solid ${(p) => p.theme.colors.border};
  color: #4a5a6c;
  svg {
    width: 20px;
    height: 20px;
  }
`;
const Head = styled.header`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  h2 {
    font-size: 18px;
    line-height: 26px;
    font-weight: 600;
  }
  span {
    font-size: 13px;
    color: ${(p) => p.theme.colors.textMuted};
  }
`;
const GroupTitle = styled.h3`
  margin: 16px 8px 4px;
  font-size: 13px;
  line-height: 18px;
  font-weight: 600;
  color: ${(p) => p.theme.colors.textMuted};
`;
const GroupBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 44px;
  margin-top: 8px;
  padding: 0 8px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: ${(p) => p.theme.colors.textMuted};
  text-align: left;
  &:hover {
    color: ${(p) => p.theme.colors.text};
  }
  svg {
    width: 16px;
    height: 16px;
    margin-left: auto;
    transition: transform ${(p) => p.theme.motion.base};
  }
  &[aria-expanded="true"] svg {
    transform: rotate(180deg);
  }
`;
const Requests = styled.section`
  display: grid;
  gap: 2px;
  padding: 4px 4px 8px;
  border-radius: 12px;
  background: ${(p) => p.theme.colors.input};
  h3 {
    margin: 8px 8px 2px;
    font-size: 13px;
    font-weight: 600;
    color: ${(p) => p.theme.colors.textSecondary};
  }
`;
const Retry = styled.div`
  display: grid;
  justify-items: start;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 12px;
  background: ${(p) => p.theme.colors.dangerSoft};
  color: ${(p) => p.theme.colors.danger};
  font-size: 14px;
  line-height: 20px;
  button {
    min-height: 36px;
    padding: 0 12px;
    border-radius: 8px;
    font-weight: 600;
    background: ${(p) => p.theme.colors.surfaceHover};
    color: ${(p) => p.theme.colors.text};
  }
`;
const Panel = styled.aside<{ $sheet?: boolean }>`
  display: grid;
  gap: 16px;
  align-content: start;
  padding: ${(p) => (p.$sheet ? "0" : "16px 12px 12px")};
  border-radius: 16px;
  background: ${(p) => (p.$sheet ? "transparent" : p.theme.colors.surface)};
  min-width: 0;
  @media (min-width: 1040px) {
    ${(p) =>
      !p.$sheet &&
      `
      position: sticky;
      top: 72px;
      max-height: calc(100svh - 96px);
      overflow-y: auto;
    `}
  }
`;

export type SocialProps = {
  friends: Person[] | null;
  friendsError: string;
  incoming: Person[];
  q: string;
  setQ: (v: string) => void;
  found: Person[];
  searching: boolean;
  ctx: InviteCtx;
  searchRef?: React.Ref<HTMLInputElement>;
  sheet?: boolean;
  onFocusSearch: () => void;
  onRetry: () => void;
  onAdd: (id: string) => void;
  onInvite: (id: string) => void;
  onRemove: (id: string) => void;
  onAccept: (id: string) => void;
  onDecline: (id: string) => void;
};

export default function Social(p: SocialProps) {
  const { t } = useLanguage();
  const [offlineOpen, setOfflineOpen] = useState(false);
  const query = p.q.trim();
  const by = (s: string) => (p.friends || []).filter((f) => (f.status || "offline") === s);
  const live = by("in_game").concat(by("online"));
  const offline = by("offline");
  const loading = p.friends === null && !p.friendsError;
  const empty = p.friends !== null && p.friends.length === 0 && !p.incoming.length;
  const row = (f: Person) => <FriendRow key={f.id} f={f} ctx={p.ctx} onInvite={() => p.onInvite(f.id)} onRemove={() => p.onRemove(f.id)} />;

  return (
    <Panel $sheet={p.sheet} aria-label={t("home.friends")}>
      {!p.sheet && (
        <Head>
          <h2>{t("home.friends")}</h2>
          {p.friends && p.friends.length > 0 && <span>{t("home.online_count", { n: live.length })}</span>}
        </Head>
      )}
      <SearchBox value={p.q} onChange={p.setQ} inputRef={p.searchRef} />

      {query ? (
        <SearchResults q={p.q} found={p.found} searching={p.searching} ctx={p.ctx} onAdd={p.onAdd} onInvite={p.onInvite} onClear={() => p.setQ("")} />
      ) : (
        <>
          {p.friendsError && !p.friends && (
            <Retry role="alert">
              {t("home.friends_load_error")}
              <button type="button" onClick={p.onRetry}>
                {t("common.retry")}
              </button>
            </Retry>
          )}
          {p.incoming.length > 0 && (
            <Requests aria-label={t("home.friend_requests")}>
              <h3>
                {t("home.friend_requests")} · {p.incoming.length}
              </h3>
              <List>
                {p.incoming.map((f) => (
                  <RequestRow key={f.id}>
                    <PlayerAvatar name={f.username} src={f.avatar} size={40} />
                    <Who>
                      <span className="nick" title={f.username}>
                        {f.username}
                      </span>
                    </Who>
                    <Actions className="req">
                      <Pill type="button" aria-label={t("home.accept_friend_named", { name: f.username })} onClick={() => p.onAccept(f.id)}>
                        {t("home.accept_short")}
                      </Pill>
                      <Pill type="button" $tone="quiet" aria-label={t("home.decline_named", { name: f.username })} onClick={() => p.onDecline(f.id)}>
                        {t("home.decline_short")}
                      </Pill>
                    </Actions>
                  </RequestRow>
                ))}
              </List>
            </Requests>
          )}

          {loading && (
            <div role="status" aria-label={t("common.loading")} style={{ display: "grid", gap: 8 }}>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} $h={56} $r={12} />
              ))}
            </div>
          )}

          {live.length > 0 && (
            <section>
              <GroupTitle>
                {t("home.status.online")} · {live.length}
              </GroupTitle>
              <List>{live.map(row)}</List>
            </section>
          )}
          {offline.length > 0 && (
            <section>
              <GroupBtn type="button" aria-expanded={offlineOpen || live.length === 0} onClick={() => setOfflineOpen((v) => !v)}>
                {t("home.status.offline")} · {offline.length}
                <FiChevronDown aria-hidden="true" />
              </GroupBtn>
              {(offlineOpen || live.length === 0) && <List>{offline.map(row)}</List>}
            </section>
          )}

          {empty && (
            <Empty>
              <div className="stack" aria-hidden="true">
                <Ghost><FiUser /></Ghost>
                <Ghost><FiUser /></Ghost>
                <Ghost><FiUser /></Ghost>
              </div>
              <h3>{t("home.friends_empty_title")}</h3>
              <p>{t("home.friends_empty_text")}</p>
              <button type="button" onClick={p.onFocusSearch}>
                {t("home.friends_empty_cta")}
              </button>
            </Empty>
          )}
        </>
      )}
    </Panel>
  );
}
